import PropTypes from "prop-types";
import * as React from "react";
import { Influencer, Product } from "../../../domain";
import { useCurrentUser } from "../../../globalState";
import {
  useDupeProductIdContext,
  useLivestreamWarningContext,
} from "../../../pages/Dashboard/ShopEditor";
import { Badge, Button, ExternalLinkIcon } from "../../../themeV2";
import { ProductButton, ProductImage, ProductText } from "../../Product";
import ProductModal from "../../ProductComposer";
import CheckoutLinkDialog from "./CheckoutLinkDialog";
import { canCreateLink } from "./checkoutLink";
import css from "./ShopBuilderProduct.module.css";

const ShopBuilderProduct = React.memo(function ShopBuilderProduct({
  onClose,
  product,
  refresh,
  salesCount,
}) {
  const currentUser = useCurrentUser();
  const [isEditing, setIsEditing] = React.useState(false);
  const [isLinking, setIsLinking] = React.useState(false);
  const { totalSales, newSales } = countsFor(salesCount);
  const { dupeProductId } = useDupeProductIdContext();
  const livestreamWarning = useLivestreamWarningContext();

  React.useEffect(() => {
    // trigger edit dialog after duplicate saved
    if (!dupeProductId) return;
    if (dupeProductId === product.productId) {
      setIsEditing(true);
    }
  }, [dupeProductId, product]);

  return (
    <div className={css.productCard}>
      <ProductButton onClick={() => setIsEditing(true)}>
        <ProductImage product={product} inset={product.aspectRatio !== 1} />
        <div className={css.pcBody}>
          <ProductText product={product} />

          <div className={css.cardFooter}>
            {hasStatusChips(product) && (
              <div className={css.statusChips}>
                {!product.isActive && !product.vaultedUntil && (
                  <Badge variant="status" status="unavailable">
                    Hidden
                  </Badge>
                )}

                {Boolean(product.vaultedUntil) && (
                  <Badge variant="status" status="vaulted">
                    Vaulted
                  </Badge>
                )}

                {product.isFeatured && (
                  <Badge variant="status" status="featured">
                    Featured
                  </Badge>
                )}

                {product.isHiddenMultiSign && (
                  <Badge variant="status" status="unavailable">
                    Hidden (Multi-Sign)
                  </Badge>
                )}
              </div>
            )}

            <div className={css.statsSurface}>
              <span>
                Stock:{" "}
                <span className={css.statsValue}>
                  {product.stock == null ? "Unlimited" : product.stock}
                </span>
                {totalSales > 0 && (
                  <>
                    {" \u00b7 Sales: "}
                    <span className={css.statsValue}>{totalSales}</span>
                    {Boolean(newSales) && ` (${newSales} pending)`}
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      </ProductButton>

      {(currentUser.isAdmin || canCreateLink(product)) && (
        <div className={css.cardActions}>
          {currentUser.isAdmin && (
            <>
              <span className={css.productId}>ID: {product.productId}</span>
              <a
                className={css.imageLink}
                href={
                  product.croppedOriginalImageUrl ?? product.originalImageUrl
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                Image <ExternalLinkIcon />
              </a>
            </>
          )}

          {canCreateLink(product) && (
            <Button
              variant="text"
              size="sm"
              className={css.createLink}
              onClick={() => setIsLinking(true)}
              aria-label={`Create link for ${product.name}`}
            >
              Create link
            </Button>
          )}
        </div>
      )}

      {isLinking && (
        <CheckoutLinkDialog
          product={product}
          onClose={() => setIsLinking(false)}
        />
      )}

      {isEditing && (
        <ProductModal
          livestreamWarning={livestreamWarning}
          productId={product.productId}
          onClose={(props) => {
            setIsEditing(false);
            onClose(props);
            refresh();
          }}
        />
      )}
    </div>
  );
});

ShopBuilderProduct.propTypes = {
  product: PropTypes.instanceOf(Product),
  influencer: PropTypes.instanceOf(Influencer),
  refresh: PropTypes.func,
  salesCount: PropTypes.shape({
    unsigned: PropTypes.number,
    signed: PropTypes.number,
  }),
  onClose: PropTypes.func,
};

function countsFor({ unsigned: pending = 0, signed: fulfilled = 0 } = {}) {
  return { totalSales: fulfilled + pending, newSales: pending };
}

function hasStatusChips(product) {
  return (
    (!product.isActive && !product.vaultedUntil) ||
    Boolean(product.vaultedUntil) ||
    product.isFeatured
  );
}

export default ShopBuilderProduct;
